import { useEffect } from "react";

const SITE_URL = "https://autlabs.in";
const DEFAULT_TITLE = "AUT labs · AI automation and custom software in Mumbai";
const DEFAULT_DESCRIPTION =
  "AUT labs is an AI automation studio in Mumbai. We build AI agents, business automation, and custom ERP and CRM systems for Indian businesses.";

function setMeta(selector, attributes) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
}

function setLink(selector, attributes) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("link");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
}

/** Adds or removes a single <script type="application/ld+json"> by id, so
 * navigating to a page without FAQ content cleans up a previous page's schema. */
function setJsonLd(id, data) {
  const existing = document.getElementById(id);
  if (!data) {
    existing?.remove();
    return;
  }
  const script = existing || document.createElement("script");
  script.id = id;
  script.type = "application/ld+json";
  script.textContent = JSON.stringify(data);
  if (!existing) document.head.appendChild(script);
}

function toAbsoluteUrl(value) {
  if (!value) {
    const path = window.location.pathname || "/";
    return new URL(path, SITE_URL).href;
  }

  return new URL(value, SITE_URL).href;
}

/**
 * Keeps the document metadata in sync with client-side navigation.
 * Existing callers only need title and description; the extra props are opt-in.
 */
export default function Seo({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  canonical,
  image = "/og.jpg",
  type = "website",
  noIndex = false,
  faq,
  schema,
}) {
  useEffect(() => {
    const canonicalUrl = toAbsoluteUrl(canonical);
    const imageUrl = toAbsoluteUrl(image);
    /* Snippet limits ride along with the index directive. This tag replaces
       the one in index.html on every navigation, so leaving them out here
       would strip them from every route except the first paint. */
    const robots = noIndex
      ? "noindex,follow"
      : "index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1";

    document.title = title;
    setMeta('meta[name="author"]', { name: "author", content: "AUT labs" });
    setMeta('meta[name="description"]', { name: "description", content: description });
    setMeta('meta[name="robots"]', { name: "robots", content: robots });
    setLink('link[rel="canonical"]', { rel: "canonical", href: canonicalUrl });

    setMeta('meta[property="og:type"]', { property: "og:type", content: type });
    setMeta('meta[property="og:title"]', { property: "og:title", content: title });
    setMeta('meta[property="og:description"]', {
      property: "og:description",
      content: description,
    });
    setMeta('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
    setMeta('meta[property="og:image"]', { property: "og:image", content: imageUrl });
    setMeta('meta[property="og:image:alt"]', {
      property: "og:image:alt",
      content: "AUT labs custom software, automation and AI studio",
    });

    setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    setMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: description,
    });
    setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: imageUrl });
    setMeta('meta[name="twitter:image:alt"]', {
      name: "twitter:image:alt",
      content: "AUT labs custom software, automation and AI studio",
    });

    setJsonLd(
      "faq-schema",
      faq && faq.length
        ? {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faq.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          }
        : null
    );

    /* Anything else a page wants to declare, under its own id so leaving the
       page removes it again. Only for things visible on that page. */
    setJsonLd("page-schema", schema || null);
  }, [canonical, description, faq, image, noIndex, schema, title, type]);

  return null;
}
